import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { MdArrowBack, MdContentCopy, MdMarkEmailRead, MdPause, MdPlayArrow, MdReplay, MdSchedule, MdSearch, MdSend, MdStop, MdWarningAmber } from 'react-icons/md';
import { getData, postData } from '../../../Webservice/Webservice';
import { FONT, LOG_STATUS, Progress, StatusChip, authToken, fmtDateTime, pct } from './emailShared';

const PAGE_SIZE = 25;

function EmailDetails() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [logStatus, setLogStatus] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmStop, setConfirmStop] = useState(false);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const params = { page, limit: PAGE_SIZE };
      if (logStatus) params.status = logStatus;
      if (search) params.search = search;
      const res = await getData(`campaign/email/${campaignId}`, params, authToken(), false);
      setData(res);
      setError('');
    } catch (err) {
      if (!quiet) setError('Failed to load campaign details.');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [campaignId, page, logStatus, search]);

  useEffect(() => { load(); }, [load]);

  const status = data?.campaign?.status;
  const live = status === 'running' || status === 'pending';
  // opens keep arriving for days after the last email, so keep refreshing (slowly) once it has finished
  const watchOpens = !live && !!data?.campaign?.trackOpens && (data?.campaign?.stats?.sent || 0) > 0;
  useEffect(() => {
    if (!live && !watchOpens) return undefined;
    const t = setInterval(() => load(true), live ? 5000 : 20000);
    return () => clearInterval(t);
  }, [live, watchOpens, load]);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const act = async (action) => {
    setBusy(true);
    try {
      await postData(`campaign/email/${campaignId}/${action}`, {}, authToken(), false);
      await load(true);
    } catch (e) {
      /* reason already shown */
    } finally {
      setBusy(false);
      setConfirmStop(false);
    }
  };

  if (loading && !data) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}><CircularProgress /></Box>;
  if (error || !data) return <Box sx={{ p: 3 }}><Button startIcon={<MdArrowBack />} onClick={() => navigate('/email')} sx={{ textTransform: 'none', fontWeight: 700 }}>Back</Button><Alert severity="error" sx={{ mt: 2 }}>{error || 'Campaign not found'}</Alert></Box>;

  const { campaign, logs, meta, lastSentAt, limits } = data;
  const s = campaign.stats || {};
  const waiting = s.pending || 0;
  // rough finish time from the configured pace and hourly cap
  const perMail = ((limits?.delayMs || 2000) + 400) / 1000;
  const secs = Math.max(waiting * perMail, waiting > (limits?.hourly || 200) ? (waiting / (limits?.hourly || 200)) * 3600 : 0);
  const eta = secs ? (secs < 90 ? 'about a minute' : secs < 5400 ? `about ${Math.round(secs / 60)} min` : `about ${(secs / 3600).toFixed(1)} h`) : '';

  const cards = [
    { label: 'Total recipients', value: s.total || 0, color: '#6366f1', bg: '#eef2ff' },
    { label: 'Sent', value: s.sent || 0, color: '#059669', bg: '#d1fae5' },
    { label: 'Waiting', value: waiting, color: '#b45309', bg: '#fef3c7' },
    { label: 'Failed', value: s.failed || 0, color: '#dc2626', bg: '#fee2e2' },
    { label: 'Skipped', value: s.skipped || 0, color: '#475569', bg: '#f1f5f9' },
    { label: 'Opened', value: campaign.trackOpens === false ? 'Not tracked' : s.sent ? `${s.opened || 0} (${pct(s.opened || 0, s.sent)}%)` : '-', color: '#0369a1', bg: '#e0f2fe' },
  ];

  const canStart = ['draft', 'scheduled', 'pending'].includes(status);
  const canStop = ['running', 'paused', 'scheduled', 'draft', 'pending'].includes(status);

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, background: '#f8fafc', minHeight: '100vh' }}>
      <Box sx={{ maxWidth: 1250, mx: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <IconButton onClick={() => navigate('/email')} aria-label="back" sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px' }}><MdArrowBack size={20} /></IconButton>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 23.5, color: '#0f172a', lineHeight: 1.2 }}>{campaign.name}</Typography>
                <StatusChip status={status} />
                {live && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#059669', fontFamily: FONT, fontSize: 11.5, fontWeight: 700 }}><CircularProgress size={12} color="inherit" /> live</Box>}
              </Box>
              <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b' }}>{campaign.campId ? `${campaign.campId} · ` : ''}{campaign.subject}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button onClick={() => navigate('/email', { state: { duplicateId: campaignId } })} startIcon={<MdContentCopy />} sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '10px', background: '#e0f2fe', color: '#075985', '&:hover': { background: '#bae6fd' } }}>Duplicate</Button>
            {canStart && <Button disabled={busy} onClick={() => act('start')} startIcon={<MdPlayArrow />} variant="contained" sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '10px', background: '#059669', '&:hover': { background: '#047857' } }}>Start now</Button>}
            {status === 'running' && <Button disabled={busy} onClick={() => act('pause')} startIcon={<MdPause />} sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '10px', background: '#ffedd5', color: '#9a3412', '&:hover': { background: '#fed7aa' } }}>Pause</Button>}
            {status === 'paused' && <Button disabled={busy} onClick={() => act('resume')} startIcon={<MdPlayArrow />} variant="contained" sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '10px', background: '#059669', '&:hover': { background: '#047857' } }}>Resume</Button>}
            {(s.failed || 0) > 0 && ['completed', 'paused'].includes(status) && <Button disabled={busy} onClick={() => act('retry-failed')} startIcon={<MdReplay />} sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '10px', background: '#e0e7ff', color: '#3730a3' }}>Retry {s.failed} failed</Button>}
            {canStop && <Button disabled={busy} onClick={() => setConfirmStop(true)} startIcon={<MdStop />} sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '10px', background: '#fee2e2', color: '#b91c1c', '&:hover': { background: '#fecaca' } }}>Stop</Button>}
          </Box>
        </Box>

        {campaign.haltReason && status === 'paused' && <Alert severity="warning" icon={<MdWarningAmber />} sx={{ mb: 2 }}>Paused automatically: {campaign.haltReason}</Alert>}
        {status === 'scheduled' && <Alert severity="info" icon={<MdSchedule />} sx={{ mb: 2 }}>Scheduled for <b>{fmtDateTime(campaign.scheduledAt)}</b>. It starts by itself; you can also start it now.</Alert>}
        {status === 'cancelled' && <Alert severity="error" sx={{ mb: 2 }}>Stopped {fmtDateTime(campaign.stoppedAt)}. {s.skipped || 0} email(s) were not sent.</Alert>}

        <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: '14px', border: '1px solid #e2e8f0' }}>
          <Progress stats={s} status={status} height={12} />
          <Box sx={{ mt: 1.5, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {[
              ['Started', campaign.startedAt ? fmtDateTime(campaign.startedAt) : '-'],
              ['Last email sent', lastSentAt ? fmtDateTime(lastSentAt) : '-'],
              ['Finished', campaign.completedAt ? fmtDateTime(campaign.completedAt) : eta && status === 'running' ? `in ${eta}` : '-'],
              ['Pace', `1 email every ~${Math.round(perMail)}s · max ${limits?.hourly || 200}/hour`],
              ...(campaign.trackOpens === false ? [] : [['Open-tracking address', campaign.publicBaseUrl ? `${campaign.publicBaseUrl}/api/email/open/…` : 'not set']]),
            ].map(([k, v]) => (
              <Box key={k}>
                <Typography sx={{ fontFamily: FONT, fontSize: 11.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{k}</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 12.5, fontWeight: 700, color: '#0f172a' }}>{v}</Typography>
              </Box>
            ))}
          </Box>
        </Paper>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 1.5, mb: 2.5 }}>
          {cards.map((c) => (
            <Paper key={c.label} elevation={0} sx={{ p: 2, borderRadius: '14px', background: c.bg }}>
              <Typography sx={{ fontFamily: FONT, fontSize: 11.5, fontWeight: 700, color: c.color, opacity: 0.85 }}>{c.label}</Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: 21.5, fontWeight: 800, color: c.color }}>{c.value}</Typography>
            </Paper>
          ))}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2.5, mb: 2.5 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e2e8f0' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5 }}>Message preview</Typography>
              <Chip size="small" label={`Spam risk ${campaign.spamScore ?? 0}/100`} sx={{ fontFamily: FONT, fontWeight: 700, background: campaign.spamLevel === 'good' ? '#dcfce7' : campaign.spamLevel === 'warning' ? '#fef3c7' : '#fee2e2', color: campaign.spamLevel === 'good' ? '#166534' : campaign.spamLevel === 'warning' ? '#92400e' : '#991b1b' }} />
            </Box>
            <Box component="iframe" title="Email preview" sandbox="" srcDoc={`<body style="font-family:Arial,sans-serif;font-size:15px;line-height:1.6;color:#111827;margin:16px">${campaign.content || ''}</body>`} sx={{ width: '100%', height: 320, border: '1px solid #e2e8f0', borderRadius: '10px', background: '#fff' }} />
            {Array.isArray(campaign.attachments) && campaign.attachments.length > 0 && (
              <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b', mt: 1.5 }}>Attachments: {campaign.attachments.map((a) => a.split('/').pop()).join(', ')}</Typography>
            )}
          </Paper>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e2e8f0' }}>
            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5, mb: 1.5 }}>How this campaign protects your sender reputation</Typography>
            {[
              'Only customers who agreed to promotional emails; anyone who unsubscribes is removed straight away.',
              'Every email carries an unsubscribe link and the one-click unsubscribe header mail apps look for.',
              'Sent slowly, one by one, and capped per hour so the mail server is never flooded.',
              'A plain-text version is included with every email, and scripts, forms and hidden code are stripped out.',
              'Each email is addressed to one person and greets them by name.',
            ].map((t) => (
              <Box key={t} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <MdMarkEmailRead size={17} color="#059669" style={{ flexShrink: 0, marginTop: 3 }} />
                <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#334155', lineHeight: 1.5 }}>{t}</Typography>
              </Box>
            ))}
            <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#94a3b8', mt: 1.5 }}>Also make sure your sending domain has SPF, DKIM and DMARC records set up with your mail provider. Those are configured outside this panel and matter most for reaching the inbox.</Typography>
          </Paper>
        </Box>

        <Paper elevation={0} sx={{ borderRadius: '14px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5 }}><MdSend style={{ verticalAlign: 'middle', marginRight: 6 }} />Delivery log</Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <TextField size="small" placeholder="Search email" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} InputProps={{ startAdornment: <MdSearch size={18} style={{ marginRight: 6, color: '#94a3b8' }} /> }} />
              <TextField select size="small" label="Status" value={logStatus} onChange={(e) => { setLogStatus(e.target.value); setPage(1); }} sx={{ minWidth: 140 }}>
                <MenuItem value="">All</MenuItem>
                {Object.entries(LOG_STATUS).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
              </TextField>
            </Box>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { backgroundColor: '#f8fafc', fontWeight: 800, py: 1.25, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#475569', fontFamily: FONT } }}>
                  <TableCell>#</TableCell><TableCell>Customer</TableCell><TableCell>Email</TableCell><TableCell>Status</TableCell><TableCell>Sent</TableCell><TableCell>Opened</TableCell><TableCell>Note</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.length === 0 ? (
                  <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 4, color: '#64748b', fontFamily: FONT }}>No entries.</TableCell></TableRow>
                ) : logs.map((l, i) => (
                  <TableRow key={l._id} hover sx={{ '& td': { fontFamily: FONT, fontSize: 12 } }}>
                    <TableCell>{(meta.page - 1) * meta.limit + i + 1}</TableCell>
                    <TableCell>{l.customerId?.name || 'N/A'}</TableCell>
                    <TableCell>{l.email}</TableCell>
                    <TableCell><StatusChip status={l.status} map={LOG_STATUS} /></TableCell>
                    <TableCell>{l.sentAt ? fmtDateTime(l.sentAt) : '-'}</TableCell>
                    <TableCell>{l.openedAt ? fmtDateTime(l.openedAt) : '-'}</TableCell>
                    <TableCell sx={{ color: l.status === 'failed' ? '#b91c1c' : '#64748b', maxWidth: 260 }}><Typography sx={{ fontFamily: FONT, fontSize: 11.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={l.error}>{l.error || ''}</Typography></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {meta.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <Pagination count={meta.totalPages} page={meta.page} shape="rounded" showFirstButton showLastButton onChange={(_, p) => setPage(p)} sx={{ '& .MuiPaginationItem-root.Mui-selected': { backgroundColor: 'rgb(255, 209, 2)', color: 'black' } }} />
            </Box>
          )}
        </Paper>
      </Box>

      <Dialog open={confirmStop} onClose={() => !busy && setConfirmStop(false)} PaperProps={{ sx: { borderRadius: '14px', p: 1, minWidth: 380 } }}>
        <DialogTitle sx={{ fontFamily: FONT, fontWeight: 800 }}>Stop this campaign?</DialogTitle>
        <DialogContent><Typography sx={{ fontFamily: FONT }}>Emails already sent can't be recalled. Everyone still waiting will not receive it, and the campaign can't be restarted. To only hold it for a while, use Pause instead.</Typography></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setConfirmStop(false)} disabled={busy} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Keep going</Button>
          <Button onClick={() => act('stop')} disabled={busy} variant="contained" sx={{ textTransform: 'none', fontWeight: 700, background: '#dc2626', '&:hover': { background: '#b91c1c' } }}>{busy ? 'Stopping…' : 'Yes, stop'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default EmailDetails;
